import { Fragment, useEffect, useCallback, useMemo, useRef } from "react";
import { Form } from "react-bootstrap";
import { useFormikContext } from "formik";
import { useDataSettings } from "../../hooks/useData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff } from "@fortawesome/free-solid-svg-icons";
import RotaryKnob from "../rotaryKnob";
import Container from "../container";
import DeduplicateRBB from "../deduplicateRBB";

import get from "lodash.get";
import unset from "lodash.unset";
import isEqual from "lodash.isequal";

export default function Results(props) {
  const maxItems = useDataSettings("maxItems");
  const { values, dirty, setFieldValue, handleChange, handleBlur } =
    useFormikContext();
  const isMulti = useMemo(() => (get(values, "blocks") || []).length > 1, [values]);
  const isFlat = useMemo(() => get(values, "results.flatAllBlocks"), [values]);

  const [max, maxMax] = useMemo(() => {
    const maxMax = values.blocks?.reduce((tot, block) => {
      const max = Number(get(block, "results.max") || 0);
      return tot + max;
    }, 0);

    return [get(values, "results.max") || 20, maxMax || maxItems];
  }, [maxItems, values]);

  const resetMax = useCallback(() => {
    const computedMax = get(values, "blocks").reduce((tot, block) => {
      const max = Number(get(block, "results.max") || 0);
      return tot + max;
    }, 0);

    setFieldValue("results.max", computedMax);
  }, [setFieldValue, values]);

  // The normalisation of the block's results (below) follows the changes the
  // user makes to what it depends on: the number of sub-blocks, flat, max,
  // the min and fallback of the first sub-block, canBeFallback. Not the open
  // of a stored row (nor the form back to it, after a Save or a Discard: not
  // modified), which it used to change: all the pages, also opened in the
  // block editor (the max of a page of several sub-blocks, set by the
  // variant page editor, was removed); nor a change of anything else (e.g.
  // the title of a page).
  const firstMin = get(values, "blocks[0].results.min");
  const firstFallback = get(values, "blocks[0].results.fallbackBlockId");
  const inputs = useMemo(
    () => ({
      isMulti,
      isFlat,
      max,
      firstMin,
      firstFallback,
      canBeFallback: values.canBeFallback
    }),
    [firstFallback, firstMin, isFlat, isMulti, max, values.canBeFallback]
  );
  const normalisedFor = useRef();

  useEffect(() => {
    const changed = !isEqual(normalisedFor.current, inputs);
    normalisedFor.current = inputs;
    if (!dirty || !changed) return;

    const newRes = { ...values.results };

    if (isMulti) {
      if (newRes.min === undefined) newRes.min = 0;

      if (!isFlat) unset(newRes, "max");
      else newRes.max = max;
    } else {
      const min = get(values, "blocks[0].results.min");
      if (newRes.min === undefined) newRes.min = min;

      const firstFallback = get(values, "blocks[0].results.fallbackBlockId");
      if (!newRes.fallbackBlockId && !values.canBeFallback && firstFallback) {
        newRes.fallbackBlockId = firstFallback;
      }

      // unset(newRes, "max");
      // const min = get(values, "blocks[0].results.min");
      // //min = newRes.min > min ? min : newRes.min;
      // newRes.min = min || 0;
      // //  newRes.flatAllBlocks = false;
      // if (!newRes.fallbackBlockId && isMulti) {
      //   const defaultFallbackId = get(values, "blocks[0].results.fallbackBlockId");
      //   if (defaultFallbackId) {
      //     newRes.fallbackBlockId = defaultFallbackId;
      //   }
      // }
    }

    if (!isEqual(newRes, values.results)) {
      setFieldValue("results", newRes);
    }
  }, [setFieldValue, max, values, isFlat, isMulti, dirty, inputs]);

  // The switches that do not apply (flat or not) or that exclude each other
  // (shuffle and smart order of the blocks) are set off: only one that is on
  // (a page with smart order got shuffleBlocks: false, absent before), when
  // the user changes flat or one of the two. Not at the open of a stored row
  // (nor the form back to it), nor at a change of anything else (e.g. the
  // title): the switches stay as stored.
  const shuffleBlocks = get(values, "results.shuffleBlocks");
  const smartOrderBlocks = get(values, "results.smartOrderBlocks");
  const switches = useMemo(
    () => ({ isFlat, shuffleBlocks, smartOrderBlocks }),
    [isFlat, shuffleBlocks, smartOrderBlocks]
  );
  const switchesFor = useRef();

  useEffect(() => {
    const changed = !isEqual(switchesFor.current, switches);
    switchesFor.current = switches;
    if (!dirty || !changed) return;

    const results = values.results ?? {};
    const off = (field) => {
      if (results[field] === true) setFieldValue(`results.${field}`, false);
    };

    if (isFlat) {
      off("shuffleBlocks");
      off("deduplicatedResultsBetweenBlocks");
      off("smartOrderBlocks");
    } else {
      off("shuffleItems");
      off("deduplicateFlattedResults");
      if (results.shuffleBlocks === true) off("smartOrderBlocks");
      if (results.smartOrderBlocks === true) off("shuffleBlocks");
    }
  }, [dirty, isFlat, setFieldValue, switches, values]);

  // if (values.blocks?.length < 2) {
  //   return <small className="text-muted">No Result Settings Available</small>;
  // }

  return (
    <Container
      title="Results"
      help="block-global-results"
      foldable={false}
      folded={false}
      className="extra-container-window"
    >
      <div className="global-results">
        <div className="result-knobs">
          <RotaryKnob
            value={Number(get(values, "results.min") || 0)}
            min={0}
            max={Number(max - 1)}
            step={1}
            label={isFlat ? "Min" : "Min Blocks"}
            labelPosition="bottom"
            skin="s10"
            onChange={(value) => setFieldValue("results.min", value)}
            className="margin-right"
          />
          {isFlat ? (
            <Fragment>
              <RotaryKnob
                value={Number(max)}
                min={1}
                max={maxMax}
                step={1}
                label="Max"
                labelPosition="bottom"
                skin="s10"
                onChange={(value) => setFieldValue("results.max", value)}
              />
              <span className={"max-label-manual"} onClick={resetMax}>
                <FontAwesomeIcon icon={faPowerOff} />
              </span>
            </Fragment>
          ) : null}
        </div>
        {values.blocks?.length >= 2 ? (
          <div className="results-switches">
            {isFlat ? (
              <Fragment>
                <Form.Check
                  type="switch"
                  checked={(values.results || {}).shuffleItems || false}
                  value="true"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name={`results.shuffleItems`}
                  id={`results.shuffleItems`}
                  label="Shuffle Results"
                />

                <Form.Check
                  type="switch"
                  checked={(values.results || {}).deduplicateFlattedResults || false}
                  value="true"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name={`results.deduplicateFlattedResults`}
                  id={`results.deduplicateFlattedResults`}
                  label="Deduplicate Results"
                />
              </Fragment>
            ) : (
              <Fragment>
                <Container
                  title={
                    <Form.Check
                      type="switch"
                      checked={(values.results || {}).shuffleBlocks || false}
                      value="true"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name={`results.shuffleBlocks`}
                      id={`results.shuffleBlocks`}
                      label="Shuffle Block Positions"
                      disabled={(values.results || {}).smartOrderBlocks || false}
                    />
                  }
                  foldable={false}
                  folded={true}
                  coloredBars={false}
                />

                <DeduplicateRBB />
              </Fragment>
            )}
          </div>
        ) : null}
      </div>
    </Container>
  );
}
